import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, DependencyList } from 'react'

export type PageAction = {
    children: ReactNode
    onClick: () => void
    disabled?: boolean
}

type PageActionContextType = {
    action: PageAction | null
    setAction: (action: PageAction | null) => void
}

const PageActionContext = createContext<PageActionContextType | undefined>(undefined)

export const usePageAction = (): PageActionContextType => {
    const context = useContext(PageActionContext)
    if (!context) {
        throw new Error('usePageAction must be used within a PageActionProvider')
    }
    return context
}

/**
 * Lets a page register its primary action (Save / Add X) to be rendered in the
 * top navbar instead of inline. `onClick` is read from a ref on every call, so
 * passing a lean `deps` array (e.g. just `[disabled]`) won't leave it stale.
 */
export const useSetPageAction = (action: PageAction | null, deps: DependencyList) => {
    const { setAction } = usePageAction()
    const actionRef = useRef(action)
    actionRef.current = action

    useEffect(() => {
        if (!actionRef.current) {
            setAction(null)
            return
        }
        setAction({ ...actionRef.current, onClick: () => actionRef.current?.onClick() })
        return () => setAction(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}

type PageActionProviderProps = {
    children: ReactNode
}

export const PageActionProvider: React.FC<PageActionProviderProps> = ({ children }) => {
    const [action, setAction] = useState<PageAction | null>(null)

    return (
        <PageActionContext.Provider value={{ action, setAction }}>
            {children}
        </PageActionContext.Provider>
    )
}
